import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, InfoOutlined } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CustomTextInput from '../../../components/CustomTextInput';
import SelectedItems from '../../../components/SelectedItems';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import {
  ACCESS_LEVEL_OPTIONS,
  ACCESS_LEVELS,
  BUSINESS_PROFILE_ACCESS_LEVEL_OPTIONS,
  BUSINESS_PROFILES_MODULE_KEY,
  DEVELOPER_DASHBOARD_MODULE_KEY,
  EINVOICING_MODULE_KEYS,
  ROLE_MODULES,
  applyDeveloperDashboardSelection,
  applyEinvoicingModuleSelection,
  buildDefaultPermissions,
  isEinvoicingModuleActive,
  mapToPermissionsPayload,
  permissionsToMap,
} from '../constants/roleModules';
import { useCreateRoleHandler } from '../dataHandlers/createRoleHandler';
import { useUpdateRoleHandler } from '../dataHandlers/updateRoleHandler';
import { useGetBusinessProfilesHandler } from '../../businessProfiles/dataHandlers/getBusinessProfilesHandler';
import BusinessProfileMultiSelect from './BusinessProfileMultiSelect';

const getProfileChipLabel = (profiles, profileId) => {
  const profile = profiles.find((item) => item.profile_id === profileId);
  if (!profile) return profileId;

  const name = profile.party_name || profile.name || '';
  const tin = profile.tin || profile.party_tax_scheme?.company_id || '';
  return [name, tin].filter(Boolean).join(' | ') || profileId;
};

export default function RoleFormModal({ open, onClose, mode, roleData }) {
  const { settings, common } = useTranslatedMessages();
  const team = settings.team ?? {};
  const modals = team.role_modal ?? {};
  const modules = team.modules ?? {};
  const accessLevels = team.access_levels ?? {};
  const ui = common.ui ?? {};
  const isView = mode === 'view';
  const isCreate = mode === 'create';

  const schema = Yup.object().shape({
    name: Yup.string().required(settings.validations?.field_required || 'This field is required'),
  });

  const [permissionMap, setPermissionMap] = useState(buildDefaultPermissions());
  const [allowedBusinessProfileIds, setAllowedBusinessProfileIds] = useState([]);
  const [businessProfilesTouched, setBusinessProfilesTouched] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: '' },
  });

  const { businessProfiles, isLoading: isProfilesLoading } = useGetBusinessProfilesHandler({
    searchQuery: { page: 0, page_size: 100 },
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setPermissionMap(roleData?.permissions ? permissionsToMap(roleData.permissions) : buildDefaultPermissions());
      setAllowedBusinessProfileIds(roleData?.business_profile_ids ?? []);
      setBusinessProfilesTouched(false);
      reset({ name: roleData?.name ?? '' });
    }
  }, [open, roleData, reset]);

  const { mutate: createRole, isPending: isCreating } = useCreateRoleHandler({ onSuccess: onClose });
  const { mutate: updateRole, isPending: isUpdating } = useUpdateRoleHandler({ onSuccess: onClose });

  const title = isCreate
    ? modals.create_title || 'Create Role'
    : isView
      ? modals.view_title || 'View Role'
      : modals.edit_title || 'Edit Role';

  const onSubmit = (data) => {
    const payload = {
      name: data.name.trim(),
      permissions: mapToPermissionsPayload(permissionMap),
      business_profile_ids: allowedBusinessProfileIds,
    };
    if (isCreate) {
      createRole(payload);
      return;
    }
    updateRole({ roleId: roleData.id, payload });
  };

  const isPending = isCreating || isUpdating;
  const businessProfilesAccessLevel = permissionMap[BUSINESS_PROFILES_MODULE_KEY] || ACCESS_LEVELS.READ;
  const developerDashboardLevel = permissionMap[DEVELOPER_DASHBOARD_MODULE_KEY] || ACCESS_LEVELS.NONE;
  const isDeveloperDashboardActive = developerDashboardLevel > ACCESS_LEVELS.NONE;
  const isEinvoicingActive =
    isEinvoicingModuleActive(permissionMap) ||
    allowedBusinessProfileIds.length > 0 ||
    businessProfilesAccessLevel === ACCESS_LEVELS.WRITE;
  const isBusinessProfileSelectDisabled = isView || isDeveloperDashboardActive;
  const isBusinessProfileAccessDisabled = isView || isDeveloperDashboardActive;
  const isDeveloperDashboardDisabled = isView || isEinvoicingActive;
  const isBusinessProfilesRequired = !isBusinessProfileSelectDisabled;
  const showBusinessProfilesError =
    isBusinessProfilesRequired && businessProfilesTouched && allowedBusinessProfileIds.length === 0;
  const isSubmitDisabled = isPending || (isBusinessProfilesRequired && allowedBusinessProfileIds.length === 0);

  const handleAccessChange = (moduleKey, value) => {
    if (moduleKey === DEVELOPER_DASHBOARD_MODULE_KEY) {
      setPermissionMap((prev) => applyDeveloperDashboardSelection(prev, value));
      if (value > ACCESS_LEVELS.NONE) {
        setAllowedBusinessProfileIds([]);
      }
      return;
    }

    if (moduleKey === BUSINESS_PROFILES_MODULE_KEY) {
      setPermissionMap((prev) => applyEinvoicingModuleSelection(prev, moduleKey, value));
      return;
    }

    if (EINVOICING_MODULE_KEYS.includes(moduleKey)) {
      setPermissionMap((prev) => applyEinvoicingModuleSelection(prev, moduleKey, value));
    }
  };

  const handleBusinessProfileIdsChange = (ids) => {
    setBusinessProfilesTouched(true);
    setAllowedBusinessProfileIds(ids);
    if (ids.length > 0) {
      setPermissionMap((prev) => ({
        ...prev,
        [DEVELOPER_DASHBOARD_MODULE_KEY]: ACCESS_LEVELS.NONE,
      }));
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '40vw' },
          maxWidth: '900px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          outline: 'none',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Divider sx={{ my: 1 }} />

        <Box sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                {modals.role_name_label || 'Role Name'}
              </Typography>
              <CustomTextInput
                name="name"
                label=""
                control={control}
                errors={errors}
                readOnly={isView}
                placeholder={modals.role_name_placeholder || 'e.g. Sales Manager'}
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {modals.permissions_label || 'Permissions'}
              </Typography>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {ROLE_MODULES.map(({ key, infoTooltip }, index) => {
                  const isEinvoicingModule = EINVOICING_MODULE_KEYS.includes(key);
                  const isModuleDisabled =
                    isView ||
                    (key === DEVELOPER_DASHBOARD_MODULE_KEY && isDeveloperDashboardDisabled) ||
                    (isEinvoicingModule && isDeveloperDashboardActive);

                  return (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        borderBottom: index < ROLE_MODULES.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2">{modules[key] || key}</Typography>
                        {infoTooltip && modules[`${key}_tooltip`] && (
                          <Tooltip title={modules[`${key}_tooltip`]} arrow>
                            <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                          </Tooltip>
                        )}
                      </Stack>
                      <FormControl size="small" sx={{ minWidth: 160 }} disabled={isModuleDisabled}>
                        <Select
                          value={permissionMap[key] ?? 0}
                          onChange={(e) => handleAccessChange(key, e.target.value)}
                          sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                        >
                          {ACCESS_LEVEL_OPTIONS.map(({ value, labelKey }) => (
                            <MenuItem key={value} value={value}>
                              {accessLevels[labelKey] || labelKey}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    {modals.business_profile_access_title || 'Business Profile Access'}
                  </Typography>
                  <Tooltip
                    title={
                      modals.business_profile_access_tooltip ||
                      'Please select a business profile to assign to this role. If no selection is made, no profile will be assigned.'
                    }
                    arrow
                  >
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </Tooltip>
                </Stack>
                <FormControl size="small" sx={{ minWidth: 160 }} disabled={isBusinessProfileAccessDisabled}>
                  <Select
                    value={businessProfilesAccessLevel}
                    onChange={(e) => handleAccessChange(BUSINESS_PROFILES_MODULE_KEY, e.target.value)}
                    sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                  >
                    {BUSINESS_PROFILE_ACCESS_LEVEL_OPTIONS.map(({ value, labelKey }) => (
                      <MenuItem key={value} value={value}>
                        {accessLevels[labelKey] || labelKey}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {modals.business_profile_access_hint ||
                  'Select the business profiles this role can access. If none are selected, no profiles are assigned.'}
              </Typography>

              <Box sx={{ mt: 1.5 }}>
                <BusinessProfileMultiSelect
                  profiles={businessProfiles}
                  selected={allowedBusinessProfileIds}
                  isLoading={isProfilesLoading}
                  disabled={isBusinessProfileSelectDisabled}
                  onChange={handleBusinessProfileIdsChange}
                  label={modals.business_profiles_label || 'Business Profiles'}
                  required={isBusinessProfilesRequired}
                  error={showBusinessProfilesError}
                  helperText={
                    showBusinessProfilesError
                      ? modals.business_profiles_required_error ||
                        'At least one Business Profile must be selected to create a role.'
                      : undefined
                  }
                  allProfilesLabel={modals.no_business_profiles || 'No Business Profiles Selected'}
                  selectedCountLabel={modals.business_profiles_selected || '{count} Business Profile(s) selected'}
                  selectAllLabel={modals.business_profiles_select_all || 'Select All {count} business profiles'}
                />
                <SelectedItems
                  items={allowedBusinessProfileIds}
                  getLabel={(profileId) => getProfileChipLabel(businessProfiles, profileId)}
                  onRemove={(profileId) =>
                    handleBusinessProfileIdsChange(allowedBusinessProfileIds.filter((id) => id !== profileId))
                  }
                />
              </Box>
            </Box>
          </Stack>
        </Box>

        {!isView && (
          <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1, mt: 2, pt: 1 }}>
            <Button onClick={onClose} disabled={isPending}>
              {modals.cancel || ui.cancel || 'Cancel'}
            </Button>
            <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitDisabled}>
              {isPending
                ? modals.processing || ui.processing || 'Processing...'
                : isCreate
                  ? modals.create_button || 'Create Role'
                  : modals.save_button || 'Save Changes'}
            </Button>
          </Stack>
        )}
      </Paper>
    </Modal>
  );
}
